import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  downloadCsv,
  formatDurationMs,
  getBehaviorExportUrl,
  getBehaviorSummary,
  type BehaviorSummary,
  type BehaviorPeriod,
} from '../services/adminAnalyticsApi';
import { ContentPageLayout } from '../components/Shared';

const BEHAVIOR_PERIODS: { value: BehaviorPeriod; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '6m', label: 'Last 6 months' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'all', label: 'All time' },
];

const MetricCard: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%' }}>
    <Typography variant="h5" color="primary" fontWeight="bold">
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Paper>
);

const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'https://app.themusic.school';

const BehaviorDashboardPage: React.FC = () => {
  const [period, setPeriod] = useState<BehaviorPeriod>('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<BehaviorSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBehaviorSummary(period);
      setData(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load behavior data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const k = data?.kpis;

  return (
    <ContentPageLayout>
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight="bold">
            Behavior
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform engagement, documents, sections, and routes.
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as BehaviorPeriod)}
            sx={{ minWidth: 160 }}
          >
            {BEHAVIOR_PERIODS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCsv(getBehaviorExportUrl(period), `behavior-${period}.csv`)}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : k ? (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={3}>
              <MetricCard label="Sessions" value={k.totalSessions} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <MetricCard label="Unique users (tracking)" value={k.uniqueActiveUsers} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <MetricCard label="Total time" value={formatDurationMs(k.totalTimeMs)} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <MetricCard label="Avg session" value={formatDurationMs(k.averageSessionDurationMs)} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Top documents
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'primary.main' }}>Document</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Views</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Time (min)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.topDocuments || []).map((d) => (
                      <TableRow key={d.documentCode}>
                        <TableCell>{d.documentCode}</TableCell>
                        <TableCell align="right">{d.views}</TableCell>
                        <TableCell align="right">{d.totalTimeMinutes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Event types
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'primary.main' }}>Event</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.routeViews || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            No event data in period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.routeViews.map((r) => (
                        <TableRow key={r.route}>
                          <TableCell>{r.route}</TableCell>
                          <TableCell align="right">{r.views}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Feature / event usage
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'primary.main' }}>Event</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.featureUsage || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            No feature events in period.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.featureUsage?.map((f) => (
                        <TableRow key={f.feature}>
                          <TableCell>{f.feature}</TableCell>
                          <TableCell align="right">{f.count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Component usage
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'primary.main' }}>Document</TableCell>
                      <TableCell sx={{ color: 'primary.main' }}>Type</TableCell>
                      <TableCell sx={{ color: 'primary.main' }}>Component</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Views</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main' }}>Time (min)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.componentUsage || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No component tracking data in period. Open documents in the app to collect views.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.componentUsage.map((c) => (
                        <TableRow key={`${c.documentId || 'unknown'}-${c.componentId}`}>
                          <TableCell>
                            {c.documentId ? (
                              <Typography
                                component="a"
                                href={`${APP_ORIGIN.replace(/\/$/, '')}/fr/doc/${c.documentId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                              >
                                {c.documentLabel || c.documentCode || c.documentId}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {c.documentLabel || '—'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {c.componentTypeLabel}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {c.componentLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {c.componentId}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{c.views}</TableCell>
                          <TableCell align="right">{c.totalTimeMinutes}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : null}
    </Box>
    </ContentPageLayout>
  );
};

export default BehaviorDashboardPage;
